import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Plus, Pencil, Trash2, ChevronDown, ClipboardList, CircleCheck, FileText, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SendQuoteDialog } from "@/components/SendQuoteDialog";
import { JobWastage } from "@/components/JobWastage";
import { JobInteractionLog } from "@/components/JobInteractionLog";
import { JobFollowups } from "@/components/JobFollowups";
import { can, isCommittedStage, isCentralRole, canEditRecord } from "@/lib/rbac";
import { useConfig } from "@/lib/useConfig";
import { PageHead, StatusBadge } from "@/components/Bits";
import { HandoffButton } from "@/components/HandoffButton";
import { ProtectionPanel } from "@/components/ProtectionPanel";
import { WarrantyCredentials } from "@/components/WarrantyCredentials";
import { BackdateField } from "@/components/BackdateField";
import { JobFeedbackCard } from "@/components/JobFeedbackCard";
import { SerialSelect } from "@/components/SerialSelect";
import { RollAllocator } from "@/components/RollAllocator";
import { WarrantyDetails } from "@/components/WarrantyDetails";
import { Switch } from "@/components/ui/switch";
import { CompleteLeadDialog } from "@/components/CompleteLeadDialog";
import { BookedDateField } from "@/components/BookedDateField";
import { EditCustomerNameButton } from "@/components/EditCustomerNameButton";
import { SendToStudioButton } from "@/components/SendToStudioButton";
import { VehicleFields } from "@/components/VehicleFields";
import { BookingCard } from "@/components/BookingCard";
import { JobStageTracker } from "@/components/JobStageTracker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const stageConfig = useConfig("pipeline-stages");
  // Always use the network's configured pipeline — never the generic built-in fallback, so a
  // freshly created studio manager sees the exact same stages as everyone else.
  const baseStatuses = stageConfig.map((s) => s.name);
  const STATUSES = baseStatuses.length && !baseStatuses.includes("Not Interested")
    ? [...baseStatuses, "Not Interested"]
    : baseStatuses;
  const [job, setJob] = useState(null);
  const committed = isCommittedStage(job?.status, STATUSES);
  const [stock, setStock] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [studios, setStudios] = useState([]);
  const [price, setPrice] = useState("");
  const [costs, setCosts] = useState({
    installer_cost: "",
    commission: "",
    gst_amount: "",
    material_cost: "",
    other_consumables_cost: "",
    other_consumables_label: "",
    dismantling_cost: "",
  });
  const [rows, setRows] = useState([{ sku_id: "", quantity: "" }]);
  const [rowsByService, setRowsByService] = useState({});
  const [svcToAdd, setSvcToAdd] = useState([]);
  const [editLine, setEditLine] = useState(null);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseBusy, setReverseBusy] = useState(false);
  const [editQty, setEditQty] = useState("");
  const [renameSvc, setRenameSvc] = useState(null);
  const [renameTo, setRenameTo] = useState("");
  const [deleteSvc, setDeleteSvc] = useState(null);
  const setSvcRows = (svc, updater) =>
    setRowsByService((m) => ({ ...m, [svc]: updater(m[svc] || [{ sku_id: "", quantity: "" }]) }));
  const canEdit = can(user, "jobs:write") && (!job || canEditRecord(user, job, "jobs:write", { allowStudioCentral: true }));
  const canWarranty = can(user, "warranty:write");
  const canSubmitWarranty = can(user, "warranty:submit");
  const [warranty, setWarranty] = useState(null);
  const [wForm, setWForm] = useState(null);
  const [sqft, setSqft] = useState("");
  const [svcCfg, setSvcCfg] = useState([]);
  const [source, setSource] = useState(null);
  // Reflect the job's current installer choice in the source toggle (once per job load).
  // Not defaulted: if no installer is assigned yet, neither option is pre-selected.
  useEffect(() => {
    if (job?.id)
      setSource(
        job.installer_id === "own" || (job.installer_type && job.installer_type !== "hq")
          ? "studio"
          : job.installer_id && job.installer_id !== "own"
          ? "hq"
          : null
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);
  const isHqFinance = ["network_admin", "accountant"].includes(user.role);
  const isStudioUser = !!user.studio_id;
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagForm, setFlagForm] = useState({ installer_id: "", amount: "", note: "" });
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeNote, setDisputeNote] = useState("");
  const [niOpen, setNiOpen] = useState(false);
  const [deliverConfirm, setDeliverConfirm] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [matOverride, setMatOverride] = useState(false);
  const [stageConfirm, setStageConfirm] = useState(null);
  const [bookDate, setBookDate] = useState("");
  const [showWastage, setShowWastage] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState("");
  const [pendingExtra, setPendingExtra] = useState({});
  const [niForm, setNiForm] = useState({ reason: "", note: "" });
  const [niReasons, setNiReasons] = useState([
    "Price Too High",
    "Went to Competitor",
    "No Response",
    "Bad Timing",
    "Changed Mind",
    "Other",
  ]);
  const [vehOpen, setVehOpen] = useState(false);
  const [vehForm, setVehForm] = useState({ make: "", model: "", colour: "", plate: "" });
  const [logMatOpen, setLogMatOpen] = useState(false);
  const [sendQuoteOpen, setSendQuoteOpen] = useState(false);
  const [matForm, setMatForm] = useState({ service: "", sku_id: "", quantity: "" });
  const [matAllocations, setMatAllocations] = useState([]);

  const loadWarranty = () =>
    api
      .get(`/jobs/${id}/warranty`)
      .then(({ data }) => setWarranty(data))
      .catch(() => setWarranty(null));

  const costsFromJob = (data) => ({
    installer_cost: String(data.installer_cost ?? 0),
    commission: String(data.commission ?? 0),
    gst_amount: String(data.gst_amount ?? 0),
    material_cost: String(data.material_cost ?? 0),
    other_consumables_cost: String(data.other_consumables_cost ?? 0),
    other_consumables_label: data.other_consumables_label ?? "",
    dismantling_cost: String(data.dismantling_cost ?? 0),
  });

  const load = () =>
    api.get(`/jobs/${id}`).then(({ data }) => {
      setJob(data);
      setPrice(String(data.price ?? ""));
      setSqft(String(data.ppf_sqft ?? ""));
      setCosts(costsFromJob(data));
    });

  const saveVehicle = async () => {
    try {
      await api.put(`/jobs/${id}`, {
        vehicle: {
          make: vehForm.make || "", model: vehForm.model || "",
          colour: vehForm.colour || "", plate: vehForm.plate || "",
        },
      });
      toast.success("Vehicle details updated");
      setVehOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveSqft = async () => {
    try {
      await api.put(`/jobs/${id}`, { ppf_sqft: Number(sqft || 0) });
      toast.success("Estimate updated from the new PPF sqft");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const autoFillPrice = async ({ silent = false } = {}) => {
    // Suggest the PPF price from the car's sqft + Standard/Premium brand list. Editable; not saved
    // until the user clicks Save. Triggered automatically when the PPF sqft changes.
    const film = (job?.services || []).find((s) => /glossy|gloss|matte|x250|x 250/i.test(s));
    if (!film) {
      if (!silent) toast.error("Add a Veloskin Glossy / Matte / X250 service to auto-fill the price");
      return;
    }
    try {
      const { data } = await api.get("/pricing/auto", {
        params: {
          make: job?.vehicle?.make || "",
          model: job?.vehicle?.model || "",
          service: film,
          ...(Number(sqft) > 0 ? { sqft: Number(sqft) } : {}),
        },
      });
      if (data.price != null) {
        setPrice(String(data.price));
        if ((!sqft || Number(sqft) === 0) && data.sqft) setSqft(String(data.sqft));
        if (!silent)
          toast.success(`Auto-filled from ${data.premium ? "Premium" : "Standard"} list · ${data.column} · ${data.tier_sqft} sqft tier — edit or Save`);
      } else if (!silent) {
        toast.error(`No auto price (${data.reason || "check the car's sqft & service"})`);
      }
    } catch (e) {
      if (!silent) toast.error(apiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    loadWarranty();
    load();
    api
      .get("/meta")
      .then(({ data }) => data.not_interested_reasons?.length && setNiReasons(data.not_interested_reasons))
      .catch(() => {});
    api.get("/config/services").then(({ data }) => setSvcCfg(data || [])).catch(() => {});
    if (canEdit && can(user, "stock:read")) {
      api.get("/stock").then(({ data }) => setStock(data)).catch(() => {});
    }
    if (canEdit && can(user, "catalog:read")) {
      api.get("/catalog").then(({ data }) => setCatalog(data || [])).catch(() => {});
    }
    if (canEdit && can(user, "scheduling:read")) {
      api.get("/teams").then(({ data }) => setInstallers(data)).catch(() => {});
    }
    if (canEdit && user.is_network) {
      api.get("/studios").then(({ data }) => setStudios(data)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-fill the suggested price on open for a PPF film job that has no price yet — no button.
  useEffect(() => {
    if (!job) return;
    const film = (job.services || []).some((s) => /glossy|gloss|matte|x250|x 250/i.test(s));
    if (film && (!job.price || Number(job.price) === 0)) autoFillPrice({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  const patch = async (payload, msg) => {
    try {
      const { data } = await api.put(`/jobs/${id}`, payload);
      setJob(data);
      setCosts(costsFromJob(data));
      toast.success(msg);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const onStatusChange = async (v) => {
    if (v === job.status) return;
    if (v === "Not Interested") {
      setNiForm({ reason: job.not_interested_reason || "", note: job.not_interested_note || "" });
      setNiOpen(true);
      return;
    }
    if (v === "Delivered") {
      setMatOverride(false);
      setReadiness(null);
      setDeliverConfirm(true);
      setReadinessLoading(true);
      api
        .get(`/jobs/${id}/material-readiness`)
        .then(({ data }) => setReadiness(data))
        .catch(() => setReadiness(null))
        .finally(() => setReadinessLoading(false));
      return;
    }
    // Guard every other stage change behind a confirmation so a mis-tap doesn't move the job.
    setBookDate((job.booked_date || "").slice(0, 10));
    setStageConfirm(v);
  };

  const doStatusUpdate = async (v, extra = {}) => {
    try {
      const { data } = await api.put(`/jobs/${id}`, { status: v, ...extra });
      setJob(data);
      setCosts(costsFromJob(data));
      if (v === "Delivered" && (data.material_pending || []).length) {
        toast.error(`Delivered — warranty on hold: ${data.material_pending.join(" · ")}`);
      } else if (v === "Delivered") {
        toast.success("Delivered — sent for warranty registration");
      } else {
        toast.success("Status updated");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (/Complete these lead details/i.test(detail)) {
        setPendingExtra(extra);
        setPendingStatus(v);
        setCompleteOpen(true);
      } else {
        toast.error(apiError(detail));
      }
    }
  };

  const applyPendingStatus = async () => {
    const { data } = await api.put(`/jobs/${id}`, { status: pendingStatus, ...pendingExtra });
    setJob(data);
    setCosts(costsFromJob(data));
    toast.success(`Moved to ${pendingStatus}`);
  };

  const saveNotInterested = async () => {
    if (!niForm.reason) return toast.error("Select a reason");
    if (niForm.reason === "Other" && !niForm.note.trim())
      return toast.error("Add a note explaining the 'Other' reason");
    try {
      const { data } = await api.put(`/jobs/${id}`, {
        status: "Not Interested",
        not_interested_reason: niForm.reason,
        not_interested_note: niForm.note,
      });
      setJob(data);
      setNiOpen(false);
      toast.success("Marked Not Interested");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const flagHq = async () => {
    try {
      const { data } = await api.post(`/jobs/${id}/flag-hq-installer`, {
        installer_id: flagForm.installer_id || null,
        amount: flagForm.amount ? Number(flagForm.amount) : null,
        note: flagForm.note,
      });
      setJob(data);
      setFlagOpen(false);
      setFlagForm({ installer_id: "", amount: "", note: "" });
      toast.success("Flagged as HQ installer — studio notified");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const respondFlag = async (action, note = "") => {
    try {
      const { data } = await api.post(`/jobs/${id}/hq-installer-response`, { action, note });
      setJob(data);
      setDisputeOpen(false);
      setDisputeNote("");
      toast.success(action === "acknowledge" ? "Acknowledged" : "Dispute sent to HQ");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const resolveFlag = async (action) => {
    try {
      const { data } = await api.post(`/jobs/${id}/resolve-hq-flag`, { action });
      setJob(data);
      toast.success(action === "uphold" ? "Flag upheld" : "Flag removed, due cancelled");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const logConsumption = async (service) => {
    const svcRows = rowsByService[service] || [];
    const items = [];
    for (const r of svcRows) {
      if (!r.sku_id) continue;
      const p = productBySku(r.sku_id);
      if (!p) continue;
      const isRoll = ["PPF", "Sunfilm"].includes(p.category);
      if (!isRoll && !p.stock_id) {
        return toast.error(`No stock for ${p.name} in this studio — add or receive stock first.`);
      }
      if (r.allocations && r.allocations.length) {
        for (const a of r.allocations) {
          if (a.serial_id && Number(a.quantity) > 0)
            items.push({ stock_id: p.stock_id || null, catalog_sku_id: p.sku_id, consumption_quantity: Number(a.quantity), serial_id: a.serial_id, service });
        }
      } else if (Number(r.quantity) > 0) {
        items.push({ stock_id: p.stock_id || null, catalog_sku_id: p.sku_id, consumption_quantity: Number(r.quantity), serial_id: null, service });
      }
    }
    if (!items.length) return toast.error("Add at least one material and quantity");
    try {
      const { data } = await api.post(`/jobs/${id}/consumption`, { items });
      setJob(data);
      setRowsByService((m) => ({ ...m, [service]: [{ sku_id: "", quantity: "" }] }));
      api.get("/stock").then(({ data: s }) => setStock(s));
      toast.success(`Consumption logged for ${service}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const submitQuickMaterial = async () => {
    const svc = matForm.service || (job.services || [])[0] || "";
    const p = productBySku(matForm.sku_id);
    if (!p) return toast.error("Select a material");
    const qty = Number(matForm.quantity);
    if (!(qty > 0)) return toast.error("Enter a quantity");
    const isRoll = ["PPF", "Sunfilm"].includes(p.category);
    if (!isRoll && !p.stock_id)
      return toast.error(`No stock for ${p.name} in this studio — add or receive stock first.`);
    let items;
    if (isRoll) {
      const allocs = (matAllocations || []).filter((a) => a.serial_id && Number(a.quantity) > 0);
      if (!allocs.length)
        return toast.error("Select the roll serial(s) this material came from");
      const allocated = allocs.reduce((s, a) => s + Number(a.quantity), 0);
      if (Math.abs(allocated - qty) > 0.01)
        return toast.error(`Allocated ${allocated} sqft must match the consumed quantity (${qty} sqft)`);
      items = allocs.map((a) => ({
        stock_id: p.stock_id || null, catalog_sku_id: p.sku_id, consumption_quantity: Number(a.quantity),
        serial_id: a.serial_id, service: svc }));
    } else {
      items = [{ stock_id: p.stock_id, catalog_sku_id: p.sku_id, consumption_quantity: qty, serial_id: null, service: svc }];
    }
    try {
      const { data } = await api.post(`/jobs/${id}/consumption`, { items });
      setJob(data);
      api.get("/stock").then(({ data: s }) => setStock(s));
      setLogMatOpen(false);
      setMatForm({ service: "", sku_id: "", quantity: "" });
      setMatAllocations([]);
      toast.success("Material logged");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };


  const doRenameService = async () => {
    if (!renameTo || renameTo === renameSvc) return toast.error("Pick a different service");
    try {
      const { data } = await api.put(`/jobs/${id}/services`, { old: renameSvc, new: renameTo });
      setJob(data);
      setRenameSvc(null);
      setRenameTo("");
      toast.success("Service updated");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const doDeleteService = async () => {
    try {
      const { data } = await api.delete(`/jobs/${id}/services/${encodeURIComponent(deleteSvc)}`);
      setJob(data);
      setDeleteSvc(null);
      api.get("/stock").then(({ data: s }) => setStock(s));
      toast.success("Service removed, consumed stock returned");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const addServices = async () => {
    if (!svcToAdd.length) return toast.error("Pick at least one service");
    try {
      const { data } = await api.post(`/jobs/${id}/services`, { services: svcToAdd });
      setJob(data);
      setSvcToAdd([]);
      toast.success("Services added");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveEditLine = async () => {
    if (!(Number(editQty) > 0)) return toast.error("Enter a valid quantity");
    try {
      const { data } = await api.put(`/jobs/${id}/consumption/${editLine.id}`, {
        consumption_quantity: Number(editQty),
      });
      setJob(data);
      setEditLine(null);
      api.get("/stock").then(({ data: s }) => setStock(s));
      toast.success("Consumption updated, stock adjusted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const reverseSerials = async () => {
    setReverseBusy(true);
    try {
      const { data } = await api.post(`/jobs/${id}/reverse-serials`);
      const { data: fresh } = await api.get(`/jobs/${id}`);
      setJob(fresh);
      api.get("/stock").then(({ data: s }) => setStock(s)).catch(() => {});
      toast.success(`Reversed ${data.reversed} roll serial(s) — balances restored`);
      setReverseOpen(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setReverseBusy(false);
    }
  };


  // Merged product list: every HQ catalog SKU, annotated with the studio's stock line
  // (quantity/stock_id) when one exists. Catalog SKUs the studio has no stock for still
  // appear (marked "no stock"), and logging them is blocked until stock is added.
  const products = (() => {
    const byId = {};
    (catalog || []).forEach((c) => {
      byId[c.id] = {
        sku_id: c.id,
        name: c.name,
        unit: c.unit,
        consumption_unit: c.consumption_unit || c.unit,
        purchase_unit_size: c.purchase_unit_size || 1,
        category: c.category,
        stock_id: null,
        quantity: 0,
      };
    });
    (stock || []).forEach((s) => {
      const cid = s.catalog_sku_id;
      if (cid && byId[cid]) {
        byId[cid] = {
          ...byId[cid],
          stock_id: s.id,
          quantity: s.quantity,
          unit: s.unit,
          consumption_unit: s.consumption_unit || s.unit,
          purchase_unit_size: s.purchase_unit_size || 1,
        };
      } else {
        byId[`stock-${s.id}`] = {
          sku_id: cid || `stock-${s.id}`,
          name: s.name,
          unit: s.unit,
          consumption_unit: s.consumption_unit || s.unit,
          purchase_unit_size: s.purchase_unit_size || 1,
          category: s.category,
          stock_id: s.id,
          quantity: s.quantity,
        };
      }
    });
    return Object.values(byId).sort((a, b) => a.name.localeCompare(b.name));
  })();
  const productBySku = (sku) => products.find((p) => p.sku_id === sku);
  const consumptionUnitOf = (sku) => productBySku(sku)?.consumption_unit || "";
  // Sunfilm material goes to the Velotint/Sunfilm service, PPF material to the PPF service.
  const serviceForMaterial = (sku) => {
    const cat = productBySku(sku)?.category;
    const svcs = job?.services || [];
    if (cat === "Sunfilm") return svcs.find((x) => /velotint|sun\s?film|tint/i.test(x));
    if (cat === "PPF") return svcs.find((x) => /ppf|veloskin/i.test(x) && !/velotint|sun\s?film/i.test(x));
    return undefined;
  };
  const conversionHint = (r) => {
    const s = productBySku(r.sku_id);
    if (!s || !Number(r.quantity)) return "";
    const size = Number(s.purchase_unit_size || 1);
    if (size === 1) return `${Number(r.quantity)} ${s.unit} will be deducted`;
    return `${Number(r.quantity)} ${s.consumption_unit} ÷ ${size} = ${(
      Number(r.quantity) / size
    ).toFixed(3)} ${s.unit} deducted`;
  };

  if (!job) return <p className="text-slate-500">Loading…</p>;
  const profit = job.profit ?? job.price - (job.material_cost || 0);
  const canEditConsumption =
    ["network_admin", "studio_manager", "studio_owner"].includes(user.role) ||
    (["central_sales_team"].includes(user.role) && job.cs_person_id === user.id);
  const hasSerialConsumption = (job.consumption || []).some((c) => c.serial_id) || !!job.has_serial_usage;
  const usesPpfSqft =
    (job.estimated_consumption || []).some((e) => e.from_ppf_sqft) ||
    svcCfg.some(
      (s) =>
        (job.services || []).includes(s.name) &&
        (s.materials || []).some((m) => m.use_ppf_sqft)
    );
  const hasFilmService = (job.services || []).some((s) =>
    /glossy|gloss|matte|x250|x 250/i.test(s)
  );
  // Warranty / Protection registration only appears from the Delivery stage onwards,
  // and only for Studio Owner, Studio Manager, Central Sales (B2C) and CRM.
  const REG_ROLES = ["studio_owner", "studio_manager", "central_sales_team", "crm"];
  const canRegister = REG_ROLES.includes(user.role);
  const deliveredIdx = STATUSES.indexOf("Delivered");
  const curStageIdx = STATUSES.indexOf(job.status);
  const deliveredReached =
    deliveredIdx >= 0 ? curStageIdx >= deliveredIdx : job.status === "Delivered" || !!job.completed_at;

  return (
    <div>
      <Link to="/jobs" className="mb-4 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-brand-ink">
        <ArrowLeft className="h-4 w-4" /> Back to jobs
      </Link>
      <PageHead
        title={job.customer_name}
        subtitle={[
          job.vehicle?.plate,
          [job.vehicle?.make, job.vehicle?.model].filter(Boolean).join(" "),
          job.vehicle?.colour,
        ].filter(Boolean).join(" · ")}
      >
        {canEdit && <EditCustomerNameButton job={job} onSaved={setJob} />}
        {["central_sales_team", "network_admin"].includes(user.role) && <SendToStudioButton job={job} />}
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            data-testid="edit-vehicle-btn"
            onClick={() => {
              const v = job.vehicle || {};
              setVehForm({ make: v.make || "", model: v.model || "", colour: v.colour || "", plate: v.plate || "" });
              setVehOpen(true);
            }}
          >
            Edit vehicle
          </Button>
        )}
        <HandoffButton user={user} record={job} kind="job" onDone={(data) => setJob(data)} />
        {["studio_owner", "studio_manager", "central_sales_team"].includes(user.role) && (
          <Button
            variant="outline"
            size="sm"
            data-testid="send-quote-btn"
            onClick={() => setSendQuoteOpen(true)}
          >
            <FileText className="mr-1.5 h-4 w-4" /> Send Quote
          </Button>
        )}
        {job.variance_flag && (
          <Badge
            data-testid="variance-flag"
            className={`border-0 ${
              job.variance_flag === "Upsell"
                ? "bg-emerald-50 text-emerald-700"
                : "bg-rose-50 text-rose-700"
            }`}
          >
            {job.variance_flag} {job.variance_pct > 0 ? "+" : ""}
            {job.variance_pct}% vs quote
          </Badge>
        )}
        <StatusBadge status={job.status} />
      </PageHead>

      <SendQuoteDialog open={sendQuoteOpen} onOpenChange={setSendQuoteOpen} job={job} />

      <Dialog open={vehOpen} onOpenChange={setVehOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-vehicle-dialog">
          <DialogHeader>
            <DialogTitle>Edit vehicle details</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <VehicleFields testidPrefix="edit-veh" value={vehForm} onChange={setVehForm} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVehOpen(false)}>Cancel</Button>
            <Button data-testid="save-vehicle-btn" onClick={saveVehicle}>Save vehicle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {(job.anomaly_flags || []).length > 0 && (
        <div className="mb-6 space-y-2" data-testid="job-anomaly-flags">
          {job.anomaly_flags.map((f) => (
            <div
              key={f}
              className={`flex items-center gap-3 rounded-md border p-3 text-sm ${
                f.startsWith("Possible wastage")
                  ? "border-amber-200 bg-amber-50 text-amber-800"
                  : "border-rose-200 bg-rose-50 text-rose-800"
              }`}
            >
              <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={1.8} />
              {f}
            </div>
          ))}
        </div>
      )}

      {(job.material_pending || []).length > 0 && (
        <div
          className="mb-6 rounded-lg border-2 border-rose-300 bg-rose-50 p-4"
          data-testid="material-pending-banner"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-rose-700">
            <AlertTriangle className="h-4 w-4" strokeWidth={2} /> Warranty on hold — material logs incomplete
          </p>
          <ul className="mt-1.5 list-disc pl-6 text-sm text-rose-800">
            {job.material_pending.map((p) => (
              <li key={p} data-testid={`material-pending-item-${p}`}>{p}</li>
            ))}
          </ul>
          <p className="mt-2 text-sm text-rose-700">
            Log the missing material consumption below. Once complete, this job is automatically
            sent to the CRM warranty queue.
          </p>
        </div>
      )}

      {job.status === "Not Interested" && (
        <div
          className="mb-6 rounded-lg border border-crimson-200 bg-crimson-50/50 p-4"
          data-testid="not-interested-banner"
        >
          <p className="flex items-center gap-2 text-sm font-semibold text-crimson-700">
            <AlertTriangle className="h-4 w-4" strokeWidth={1.8} /> Marked Not Interested
          </p>
          <p className="mt-1.5 text-sm text-brand-ink">
            <span className="font-medium">Reason:</span> {job.not_interested_reason || "—"}
          </p>
          {job.not_interested_note && (
            <p className="mt-1 text-sm text-slate-600">{job.not_interested_note}</p>
          )}
        </div>
      )}


      {(() => {
        const flag = job.hq_installer_flag || "";
        const showFlagBtn =
          isHqFinance && !flag && (job.installer_type || "studio") !== "hq";
        if (!flag && !showFlagBtn) return null;
        const tint = {
          flagged: "border-amber-200 bg-amber-50 text-amber-900",
          acknowledged: "border-blue-200 bg-blue-50 text-blue-900",
          disputed: "border-rose-200 bg-rose-50 text-rose-900",
          resolved: "border-emerald-200 bg-emerald-50 text-emerald-900",
          reverted: "border-slate-200 bg-slate-50 text-slate-700",
        }[flag] || "border-slate-200 bg-slate-50 text-slate-700";
        return (
          <div className={`mb-6 rounded-md border p-4 ${tint}`} data-testid="hq-installer-flag-banner">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                {!flag && (
                  <span>
                    Studio logged their <strong>own installer</strong> for this job. If HQ actually did
                    the installation, flag it to raise an HQ-installer due.
                  </span>
                )}
                {flag === "flagged" && (
                  <span>
                    <strong>HQ flagged this as an HQ installer.</strong>
                    {job.hq_installer_name ? ` Installer: ${job.hq_installer_name}.` : ""}
                    {job.hq_installer_flag_note ? ` Note: ${job.hq_installer_flag_note}.` : ""} A due of{" "}
                    <strong>{money(job.installer_cost || 0)}</strong> has been raised.
                  </span>
                )}
                {flag === "acknowledged" && (
                  <span>Studio <strong>acknowledged</strong> the HQ-installer flag. Due stands.</span>
                )}
                {flag === "disputed" && (
                  <span>
                    Studio <strong>disputed</strong> the HQ-installer flag.
                    {job.hq_installer_dispute_note ? ` "${job.hq_installer_dispute_note}"` : ""}
                  </span>
                )}
                {flag === "resolved" && <span>HQ-installer flag <strong>upheld by HQ</strong>. Due stands.</span>}
                {flag === "reverted" && <span>HQ-installer flag was <strong>removed</strong>. No due.</span>}
              </div>
              <div className="flex gap-2">
                {showFlagBtn && (
                  <Button
                    size="sm"
                    data-testid="flag-hq-installer-btn"
                    onClick={() => {
                      setFlagForm({ installer_id: "", amount: String(job.installer_cost || ""), note: "" });
                      setFlagOpen(true);
                    }}
                  >
                    Flag as HQ installer
                  </Button>
                )}
                {isStudioUser && flag === "flagged" && (
                  <>
                    <Button size="sm" variant="outline" data-testid="ack-flag-btn"
                      onClick={() => respondFlag("acknowledge")}>
                      Acknowledge
                    </Button>
                    <Button size="sm" variant="outline"
                      className="border-rose-200 text-rose-600 hover:bg-rose-50"
                      data-testid="dispute-flag-btn" onClick={() => setDisputeOpen(true)}>
                      Dispute
                    </Button>
                  </>
                )}
                {isHqFinance && flag === "disputed" && (
                  <>
                    <Button size="sm" data-testid="uphold-flag-btn" onClick={() => resolveFlag("uphold")}>
                      Uphold due
                    </Button>
                    <Button size="sm" variant="outline" data-testid="revert-flag-btn"
                      onClick={() => resolveFlag("revert")}>
                      Remove flag
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {can(user, "inspection:read") && (
        <Link to={`/jobs/${job.id}/inspection`} data-testid="open-inspection-btn" className="mb-4 block">
          <Button className="min-h-[44px] w-full justify-center gap-2 bg-brand-ink text-white hover:bg-brand-ink/90">
            <ClipboardList className="h-4 w-4" /> Vehicle inspection report
          </Button>
        </Link>
      )}

      <Section title="Job Details" testid="section-job-details">
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="border-brand-line shadow-none lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-lg">Job details</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2" data-testid="job-details-card">
            <div>
              <Label>Status</Label>
              {canEdit ? (
                <Select value={job.status} onValueChange={onStatusChange}>
                  <SelectTrigger data-testid="job-detail-status-select" className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="mt-1.5 capitalize">{job.status}</p>
              )}
            </div>
            {job.status === "Booked" && <BookedDateField job={job} canEdit={canEdit} onSaved={setJob} />}
            {committed && (
            <div data-testid="job-detail-installer-block">
              <Label>PPF Installer</Label>
              {canEdit ? (
                <>
                  <div className="mt-1.5 flex gap-2">
                    {[
                      ["hq", "HQ Installer"],
                      ["studio", "Own Installer"],
                    ].map(([k, label]) => (
                      <button
                        key={k}
                        data-testid={`installer-source-${k}`}
                        onClick={() => {
                          setSource(k);
                          // "Own installers" completes the field on its own — no person needed.
                          if (k === "studio" && job.installer_id !== "own")
                            patch({ installer_id: "own" }, "Own Installer assigned");
                        }}
                        className={`min-h-[36px] rounded px-3 text-xs font-medium transition-colors duration-200 ${
                          source === k
                            ? "bg-brand text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {source === "hq" ? (
                    <>
                      <Select
                        value={job.installer_id && job.installer_id !== "own" ? job.installer_id : ""}
                        onValueChange={(v) => patch({ installer_id: v }, "PPF Installer assigned")}
                      >
                        <SelectTrigger data-testid="job-detail-installer-select" className="mt-1.5">
                          <SelectValue placeholder="Assign" />
                        </SelectTrigger>
                        <SelectContent>
                          {installers
                            .filter((i) => (i.installer_type || "studio") === "hq")
                            .map((i) => (
                              <SelectItem key={i.id} value={i.id}>
                                {i.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <p className="mt-1.5 text-xs text-slate-500">
                        Shared crews contracted by HQ.
                      </p>
                    </>
                  ) : source === "studio" ? (
                    <p
                      className="mt-2 flex items-center gap-1.5 text-sm font-medium text-emerald-700"
                      data-testid="own-installer-selected"
                    >
                      <CircleCheck className="h-4 w-4" /> Own Installer — no specific person needed.
                    </p>
                  ) : (
                    <p
                      className="mt-2 text-xs text-slate-500"
                      data-testid="installer-not-selected"
                    >
                      Choose <strong>HQ Installer</strong> or <strong>Own Installer</strong>.
                    </p>
                  )}
                </>
              ) : (
                <p className="mt-1.5">{job.installer_name || "Unassigned"}</p>
              )}
            </div>
            )}
            {canEdit && user.is_network && (
              <div data-testid="job-detail-studio-block">
                <Label>Studio</Label>
                <Select
                  value={job.studio_id || ""}
                  onValueChange={(v) => patch({ studio_id: v }, "Studio assigned")}
                >
                  <SelectTrigger data-testid="job-detail-studio-select" className="mt-1.5">
                    <SelectValue placeholder="Assign a studio" />
                  </SelectTrigger>
                  <SelectContent>
                    {studios.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`job-studio-option-${s.id}`}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1.5 text-xs text-slate-500">
                  {job.studio_name
                    ? `Currently handled by ${job.studio_name}.`
                    : "This lead has no studio yet — assign one to move it forward."}
                </p>
              </div>
            )}
            <div data-testid="job-assigned-to">
              <Label>Assigned to</Label>
              <p className="mt-1.5 font-medium text-brand-ink">{job.assigned_to || "Unassigned"}</p>
            </div>

            {job.cs_person_name && (
              <div data-testid="job-cs-person">
                <Label>Central Sales person</Label>
                <p className="mt-1.5">{job.cs_person_name}</p>
                <p className="mt-1 text-xs text-slate-500">
                  Sales acquisition attributed to this person.
                </p>
              </div>
            )}
            {hasFilmService && (
              <div data-testid="jd-ppf-sqft-block">
                <Label>PPF sqft for this car</Label>
                <Input
                  className="mt-1.5 min-h-[44px]"
                  inputMode="decimal"
                  data-testid="job-detail-sqft-input"
                  value={sqft}
                  disabled={!canEdit}
                  onChange={(e) => setSqft(e.target.value)}
                  onBlur={() => autoFillPrice({ silent: true })}
                />
                <p className="mt-1 text-xs text-slate-500">
                  Pre-filled from the {job?.vehicle?.make} {job?.vehicle?.model} configuration.
                  Editing it automatically re-calculates the suggested price below — click Save on
                  the price to keep it.
                </p>
              </div>
            )}
            <div>
              <Label>Final price (₹)</Label>
              <div className="mt-1.5 flex gap-2">
                <Input
                  className="min-h-[44px] flex-1 min-w-0"
                  data-testid="job-detail-price-input"
                  value={price}
                  disabled={!canEdit}
                  onChange={(e) => setPrice(e.target.value)}
                />
                {canEdit && (
                  <Button
                    data-testid="job-save-price-btn"
                    variant="outline"
                    className="min-h-[44px] shrink-0"
                    onClick={() =>
                      patch(
                        hasFilmService && sqft !== ""
                          ? { price: Number(price || 0), ppf_sqft: Number(sqft || 0) }
                          : { price: Number(price || 0) },
                        "Price updated"
                      )
                    }
                  >
                    Save
                  </Button>
                )}
              </div>
            </div>
            <div className="sm:col-span-2" data-testid="jd-services-block">
              <Label>Services opted</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {(job.services || []).length === 0 ? (
                  <span className="text-sm text-slate-500">—</span>
                ) : (
                  (job.services || []).map((s) => (
                    <span
                      key={s}
                      className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium"
                      data-testid={`jd-service-chip-${s}`}
                    >
                      {s}
                      {canEdit && (
                        <>
                          <button
                            data-testid={`jd-edit-service-${s}`}
                            title="Change service"
                            onClick={() => { setRenameSvc(s); setRenameTo(""); }}
                            className="text-slate-400 transition-colors hover:text-brand"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button
                            data-testid={`jd-delete-service-${s}`}
                            title="Remove service"
                            onClick={() => setDeleteSvc(s)}
                            className="text-slate-400 transition-colors hover:text-red-600"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </>
                      )}
                    </span>
                  ))
                )}
              </div>
              {canEdit && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Select value="" onValueChange={(v) => setSvcToAdd((a) => (a.includes(v) ? a : [...a, v]))}>
                    <SelectTrigger className="h-8 min-w-[180px] text-xs" data-testid="jd-add-service-select">
                      <SelectValue placeholder="Add a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {svcCfg.filter((s) => !(job.services || []).includes(s.name)).map((s) => (
                        <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {svcToAdd.map((s) => (
                    <span key={s} className="flex items-center gap-1 rounded-full bg-brand-ink/10 px-2 py-0.5 text-xs">
                      {s}
                      <button onClick={() => setSvcToAdd((a) => a.filter((x) => x !== s))} className="text-slate-500">×</button>
                    </span>
                  ))}
                  {svcToAdd.length > 0 && (
                    <Button size="sm" className="h-8" data-testid="jd-add-services-btn" onClick={addServices}>
                      Add
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="sm:col-span-2" data-testid="jd-material-log">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label>Material consumption</Label>
                {canEdit && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-9 shrink-0"
                    data-testid="jd-log-material-btn"
                    onClick={() => {
                      setMatForm({ service: (job.services || [])[0] || "", sku_id: "", quantity: "" });
                      setMatAllocations([]);
                      setLogMatOpen(true);
                    }}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" /> Log Material
                  </Button>
                )}
              </div>
              <div className="mt-1.5 space-y-1">
                {(job.consumption || []).length === 0 ? (
                  <p className="text-sm text-slate-500">No materials logged yet.</p>
                ) : (
                  (job.consumption || []).map((c, i) => (
                    <div
                      key={c.id || i}
                      className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2.5 py-1.5 text-sm"
                      data-testid={`jd-consumption-${c.id || i}`}
                    >
                      <span className="min-w-0 truncate">
                        <span className="font-medium text-brand-ink">{c.name}</span>
                        <span className="text-slate-500">
                          {" · "}{Number(c.quantity).toFixed(2)} {c.unit}
                          {c.service ? ` · ${c.service}` : ""}
                        </span>
                      </span>
                      {canEditConsumption && c.id && (
                        <button
                          data-testid={`jd-edit-consumption-${c.id}`}
                          title="Edit quantity"
                          onClick={() => { setEditLine(c); setEditQty(String(c.consumption_quantity ?? c.quantity)); }}
                          className="shrink-0 text-slate-400 transition-colors hover:text-brand"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
              {isHqFinance && hasSerialConsumption && (
                <button
                  data-testid="jd-reverse-serials-btn"
                  onClick={() => setReverseOpen(true)}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-rose-600 hover:underline"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Reverse roll-serial consumption
                </button>
              )}
            </div>
            <div>
              <Label>Created</Label>
              <p className="mt-1.5 text-sm">{fmtDate(job.created_at)}</p>
            </div>
            <div>
              <Label>Completed</Label>
              <p data-testid="job-completed-date" className="mt-1.5 text-sm">
                {fmtDate(job.completed_at)}
              </p>
            </div>
          </CardContent>
        </Card>

        {canEdit && (
          <div className="mt-4" data-testid="job-wastage-block">
            <Button
              variant="outline"
              size="sm"
              className="min-h-[40px]"
              data-testid="toggle-wastage-btn"
              onClick={() => setShowWastage((v) => !v)}
            >
              {showWastage ? "Hide wastage log" : "Log wastage"}
            </Button>
            {showWastage && (
              <div className="mt-3">
                <JobWastage
                  job={job}
                  canEdit={canEdit}
                  installers={installers.filter(
                    (i) => !job.installer_id || i.id === job.installer_id || i.installer_type === "studio"
                  )}
                  onChange={load}
                />
              </div>
            )}
          </div>
        )}

      </div>
      </Section>

      <Section title="Warranty Details" defaultOpen={false} testid="section-warranty-details">
        <div className="space-y-6">
          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brand-line bg-brand-tint/40 p-4"
            data-testid="protection-opted-toggle-row"
          >
            <div>
              <p className="text-sm font-semibold text-brand-ink">Protection Plan Opted</p>
              <p className="mt-0.5 text-xs text-slate-500">
                Mark this if the customer wants the 2-Year Protection Plan. It appears on the CRM
                Protection Plan pending list once the job reaches Delivery.
              </p>
            </div>
            <Switch
              data-testid="protection-opted-switch"
              checked={!!job.protection_opted}
              disabled={!canEdit}
              onCheckedChange={async (v) => {
                try {
                  await api.patch(`/jobs/${id}/protection-opted`, { opted: v });
                  setJob((j) => ({ ...j, protection_opted: v }));
                  toast.success(v ? "Marked as Protection Plan opted" : "Protection Plan opt-out saved");
                } catch (e) {
                  toast.error(apiError(e.response?.data?.detail) || "Could not update");
                }
              }}
            />
          </div>
          {(warranty?.products?.length ? warranty.products : [null]).map((p, i) => (
            <WarrantyDetails
              key={p?.product_type || `all-${i}`}
              jobId={id}
              productType={p?.product_type}
              label={p?.label}
              status={p?.status}
              certificateNo={p?.certificate_no}
              warrantyId={p?.warranty_id}
              canSend={canWarranty}
              onChanged={loadWarranty}
            />
          ))}
        </div>
      </Section>

      <Section title="Interaction Log" testid="section-interaction-log">
        <div className="grid gap-6 lg:grid-cols-2">
          <JobInteractionLog jobId={id} phone={job.customer_phone} />
          <JobFollowups jobId={id} />
        </div>
      </Section>

      <Section title="Revenue and Profit" testid="section-revenue-profit">
      <Card className="border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">Profit breakdown</CardTitle>
          <p className="mt-1 text-xs text-slate-500">
            Internal only — GST is shown separately and never counted in profit. Net profit = base price (ex-GST) less all five costs.
          </p>
        </CardHeader>
        <CardContent className="space-y-2.5 text-sm" data-testid="job-profit-card">
          {(() => {
            const price = job.price || 0;
            const material = job.material_cost || 0;
            const gst = Number(job.gst_amount || 0);
            const base = +(price - gst).toFixed(2);
            const installer = Number((canEdit ? costs.installer_cost : job.installer_cost) || 0);
            const commission = Number((canEdit ? costs.commission : job.commission) || 0);
            const other = Number((canEdit ? costs.other_consumables_cost : job.other_consumables_cost) || 0);
            const dismantling = Number((canEdit ? costs.dismantling_cost : job.dismantling_cost) || 0);
            const net = +(base - material - installer - commission - other - dismantling).toFixed(2);
            const Row = ({ label, value, testid, strong, minus }) => (
              <div className="flex items-center justify-between gap-2">
                <span className={strong ? "font-semibold" : "text-slate-500"}>{minus ? "Less " : ""}{label}</span>
                <span data-testid={testid} className="font-semibold">{minus ? "− " : ""}{money(value)}</span>
              </div>
            );
            const EditRow = ({ label, field, testid, hint }) => (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <span className="text-slate-500">Less {label}</span>
                  {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <span className="text-slate-400">₹</span>
                  <Input inputMode="decimal" data-testid={testid} className="h-9 w-24 text-right sm:w-28"
                    value={costs[field]} onChange={(e) => setCosts((c) => ({ ...c, [field]: e.target.value }))} />
                </div>
              </div>
            );
            return (
              <>
                <Row label="Final price (GST inclusive)" value={price} testid="job-final-price" strong />
                <div className="flex items-center justify-between gap-3" data-testid="job-gst-row">
                  <div className="min-w-0">
                    <span className="text-slate-500">GST amount</span>
                    <p className="text-[11px] text-slate-400">Auto-calculated from price × studio GST %</p>
                  </div>
                  <span data-testid="job-gst" className="shrink-0 font-semibold">{money(job.gst_amount || 0)}</span>
                </div>
                <div className="flex justify-between border-y border-brand-line py-2">
                  <span className="font-semibold">Base price (ex-GST)</span>
                  <span data-testid="job-base-price" className="font-semibold">{money(base)}</span>
                </div>

                <div className="flex items-center justify-between gap-3" data-testid="job-material-cost-row">
                  <div className="min-w-0">
                    <span className="text-slate-500">Less material consumption</span>
                    <p className="text-[11px] text-slate-400">Auto from the material log in Job Details</p>
                  </div>
                  <span data-testid="job-material-cost" className="shrink-0 font-semibold">− {money(material)}</span>
                </div>

                {canEdit ? (
                  <>
                    <EditRow label="installer cost" field="installer_cost" testid="job-installer-cost-input" />
                    <EditRow label="sales commission" field="commission" testid="job-commission-input" />
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <span className="text-slate-500">Less other consumables</span>
                        <Input placeholder="What was used (masking tape, gloves…)" data-testid="job-other-consumables-label-input"
                          className="mt-1 h-8 text-xs" value={costs.other_consumables_label}
                          onChange={(e) => setCosts((c) => ({ ...c, other_consumables_label: e.target.value }))} />
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <span className="text-slate-400">₹</span>
                        <Input inputMode="decimal" data-testid="job-other-consumables-cost-input" className="h-9 w-24 text-right sm:w-28"
                          value={costs.other_consumables_cost} onChange={(e) => setCosts((c) => ({ ...c, other_consumables_cost: e.target.value }))} />
                      </div>
                    </div>
                    <EditRow label="dismantling cost" field="dismantling_cost" testid="job-dismantling-cost-input"
                      hint="Removing existing film/coating before installation." />
                  </>
                ) : (
                  <>
                    <Row label="installer cost" value={installer} testid="job-installer-cost" minus />
                    <Row label="sales commission" value={commission} testid="job-commission" minus />
                    <Row label={`other consumables${job.other_consumables_label ? ` · ${job.other_consumables_label}` : ""}`} value={other} testid="job-other-consumables-cost" minus />
                    <Row label="dismantling cost" value={dismantling} testid="job-dismantling-cost" minus />
                  </>
                )}

                <div className="flex justify-between border-t border-brand-line pt-3">
                  <span className="font-semibold">
                    Net profit {canEdit && <span className="text-xs font-normal text-slate-400">(live)</span>}
                  </span>
                  <span data-testid="job-profit"
                    className={`font-display text-xl font-semibold ${net >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {money(net)}
                  </span>
                </div>

                {canEdit && (
                  <Button className="mt-2 min-h-[44px] w-full" variant="outline" data-testid="job-save-costs-btn"
                    onClick={() => patch({
                      installer_cost: Number(costs.installer_cost || 0),
                      commission: Number(costs.commission || 0),
                      other_consumables_cost: Number(costs.other_consumables_cost || 0),
                      other_consumables_label: costs.other_consumables_label || "",
                      dismantling_cost: Number(costs.dismantling_cost || 0),
                    }, "Costs updated")}>
                    Save costs
                  </Button>
                )}
              </>
            );
          })()}
        </CardContent>
      </Card>
      </Section>

      {deliveredReached && canRegister && !canSubmitWarranty && (
        <div className="mb-6" data-testid="protection-plan-onpage">
          <ProtectionPanel job={job} />
        </div>
      )}

      <Section title="More Details" defaultOpen={false} testid="section-more-details">
      <h2 className="font-display mb-3 mt-6 text-lg font-semibold">Expected material use</h2>
      <Card className="border-brand-line shadow-none" data-testid="expected-consumption-card">
        <CardContent className="space-y-3 p-4">
          {usesPpfSqft && (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label>PPF sqft for this car</Label>
                <Input
                  className="mt-1.5 w-32 min-h-[44px]"
                  inputMode="decimal"
                  data-testid="job-ppf-sqft-input"
                  value={sqft}
                  onChange={(e) => setSqft(e.target.value)}
                  onBlur={() => autoFillPrice({ silent: true })}
                />
              </div>
              <Button
                variant="outline"
                className="min-h-[44px]"
                data-testid="save-job-sqft-btn"
                onClick={saveSqft}
              >
                Update estimate
              </Button>
              <p className="w-full text-xs text-slate-500">
                Pre-filled from the car model configuration. Editing it re-calculates the expected
                film usage and automatically refreshes the suggested price (editable — click Save on
                the price to keep it). On completion these quantities are deducted from studio stock
                in decimals.
              </p>
            </div>
          )}
          {(job.estimated_consumption || []).length === 0 ? (
            <p className="text-sm text-slate-500">
              No consumption template configured for these services.
            </p>
          ) : (
            <div className="space-y-1.5" data-testid="expected-consumption-list">
              {(job.estimated_consumption || []).map((e) => (
                <p key={e.name} className="flex justify-between text-sm">
                  <span className="text-slate-600">
                    {e.name}
                    {e.from_ppf_sqft ? " · from PPF sqft" : ""}
                  </span>
                  <span className="font-medium text-brand-ink">
                    {Number(e.quantity).toFixed(2)} {e.unit}
                  </span>
                </p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>


      <BookingCard job={job} user={user} onChanged={load} />

      {deliveredReached && canRegister && canSubmitWarranty && (
        <WarrantyCredentials job={job} onChanged={loadWarranty} />
      )}

      {deliveredReached &&
        warranty &&
        !warranty.products?.length &&
        (warranty.warranty || (canRegister && warranty.eligible.length > 0)) && (
        <Card className="mt-4 border-brand-line shadow-none" data-testid="warranty-card">
          <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
            <CardTitle className="text-base">Warranty</CardTitle>
            {canRegister && canWarranty && !warranty.warranty && (
              <Button
                size="sm"
                variant="outline"
                className="min-h-[44px]"
                data-testid="register-warranty-btn"
                onClick={() =>
                  setWForm({
                    job_id: id,
                    warranty_type:
                      warranty.warranty?.warranty_type || warranty.eligible[0]?.type || "PPF",
                    start_date:
                      warranty.warranty?.start_date ||
                      (job.completed_at || new Date().toISOString()).slice(0, 10),
                    duration_months:
                      warranty.warranty?.duration_months || warranty.eligible[0]?.months || 12,
                    notes: warranty.warranty?.notes || "",
                  })
                }
              >
                {warranty.warranty ? "Update warranty" : "Register warranty"}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {warranty.warranty ? (
              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Certificate no." value={warranty.warranty.certificate_no} testid="warranty-cert" />
                <Field label="Type" value={warranty.warranty.warranty_type} />
                <Field
                  label="Assigned CRM staff"
                  value={warranty.assigned_staff || "Unassigned"}
                  testid="warranty-assigned-staff"
                />
                <Field label="Start" value={warranty.warranty.start_date} />
                <Field label="Duration" value={`${warranty.warranty.duration_months} months`} />
                <Field label="Expiry" value={warranty.warranty.expiry_date} testid="warranty-expiry" />
                {warranty.warranty.notes && (
                  <div className="sm:col-span-3">
                    <Field label="Notes" value={warranty.warranty.notes} />
                  </div>
                )}
              </div>
            ) : (
              <p
                className="text-sm text-slate-600"
                data-testid="warranty-pending-warning"
              >
                {job.status === "Delivered" || job.completed_at
                  ? `Warranty registration pending — being handled by central CRM team.${
                      warranty.assigned_staff && canWarranty
                        ? ` Assigned to ${warranty.assigned_staff}.`
                        : ""
                    }`
                  : `Warrantable service on this job: ${warranty.eligible
                      .map((e) => `${e.service} (${e.months} months)`)
                      .join(", ")}`}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {deliveredReached && <JobFeedbackCard jobId={id} />}

      <Dialog open={!!wForm} onOpenChange={(o) => !o && setWForm(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Warranty registration</DialogTitle>
          </DialogHeader>
          {wForm && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Warranty type</Label>
                <Select
                  value={wForm.warranty_type}
                  onValueChange={(v) => {
                    const hit = warranty.eligible.find((e) => e.type === v);
                    setWForm((f) => ({
                      ...f,
                      warranty_type: v,
                      duration_months: hit?.months || f.duration_months,
                    }));
                  }}
                >
                  <SelectTrigger className="mt-1.5 min-h-[44px]" data-testid="warranty-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {warranty.eligible.map((e) => (
                      <SelectItem key={e.service} value={e.type}>
                        {e.type} — {e.service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Start date</Label>
                <Input
                  type="date"
                  className="mt-1.5 min-h-[44px]"
                  data-testid="warranty-start-input"
                  value={wForm.start_date}
                  onChange={(e) => setWForm((f) => ({ ...f, start_date: e.target.value }))}
                />
              </div>
              <div>
                <Label>Duration (months)</Label>
                <Input
                  className="mt-1.5 min-h-[44px]"
                  inputMode="numeric"
                  data-testid="warranty-duration-input"
                  value={wForm.duration_months}
                  onChange={(e) => setWForm((f) => ({ ...f, duration_months: e.target.value }))}
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Notes</Label>
                <Input
                  className="mt-1.5 min-h-[44px]"
                  data-testid="warranty-notes-input"
                  value={wForm.notes}
                  onChange={(e) => setWForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>
            </div>
          )}
          {wForm && (
            <BackdateField
              value={wForm.entry_date}
              onChange={(v) => setWForm((f) => ({ ...f, entry_date: v }))}
            />
          )}
          <DialogFooter>
            <Button
              className="min-h-[44px]"
              data-testid="save-warranty-btn"
              onClick={async () => {
                try {
                  await api.post("/warranties", {
                    ...wForm,
                    duration_months: Number(wForm.duration_months || 0),
                  });
                  toast.success("Warranty registered");
                  setWForm(null);
                  loadWarranty();
                } catch (e) {
                  toast.error(apiError(e.response?.data?.detail));
                }
              }}
            >
              Save warranty
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </Section>

      <div className="mt-4">
        <JobStageTracker
          stages={STATUSES}
          current={job.status}
          canEdit={canEdit}
          onChange={onStatusChange}
        />
      </div>

      <Dialog open={logMatOpen} onOpenChange={setLogMatOpen}>
        <DialogContent className="sm:max-w-md" data-testid="jd-log-material-dialog">
          <DialogHeader>
            <DialogTitle>Log material consumption</DialogTitle>
            <DialogDescription>
              Deducts from this studio's stock and updates the job's material cost.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(job.services || []).length > 0 && (
              <div>
                <Label className="text-xs">Service</Label>
                <Select value={matForm.service} onValueChange={(v) => setMatForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger className="mt-1" data-testid="jd-log-material-service">
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {(job.services || []).map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">Material</Label>
              <Select value={matForm.sku_id} onValueChange={(v) => {
                setMatForm((f) => ({ ...f, sku_id: v, service: serviceForMaterial(v) || f.service }));
                setMatAllocations([]);
              }}>
                <SelectTrigger className="mt-1" data-testid="jd-log-material-select">
                  <SelectValue placeholder="Select material" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.sku_id} value={p.sku_id}>
                      {p.name} {p.stock_id
                        ? `(${p.quantity.toFixed(2)} ${p.unit} left)`
                        : ["PPF", "Sunfilm"].includes(p.category) ? "(tracked by roll serial)" : "(no stock)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">
                Quantity {consumptionUnitOf(matForm.sku_id) ? `(${consumptionUnitOf(matForm.sku_id)})` : ""}
              </Label>
              <Input
                className="mt-1"
                inputMode="decimal"
                data-testid="jd-log-material-qty"
                value={matForm.quantity}
                onChange={(e) => setMatForm((f) => ({ ...f, quantity: e.target.value }))}
              />
            </div>
            {["PPF", "Sunfilm"].includes(productBySku(matForm.sku_id)?.category) && (
              <div data-testid="jd-log-material-rolls">
                <Label className="text-xs">
                  Roll serial(s) — required. Allocate the {matForm.quantity || 0} sqft consumed to the
                  roll(s) it came from.
                </Label>
                <div className="mt-1.5">
                  <RollAllocator
                    studioId={job.studio_id}
                    category={productBySku(matForm.sku_id)?.category}
                    skuId={matForm.sku_id}
                    totalQty={Number(matForm.quantity) || 0}
                    value={matAllocations}
                    onChange={setMatAllocations}
                    testidPrefix="jd-mat-roll"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogMatOpen(false)}>Cancel</Button>
            <Button data-testid="jd-log-material-save" onClick={submitQuickMaterial}>Log material</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editLine} onOpenChange={(o) => !o && setEditLine(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-consumption-dialog">
          <DialogHeader><DialogTitle>Edit consumption</DialogTitle></DialogHeader>
          {editLine && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{editLine.name} · {editLine.service || "General"}</p>
              <div>
                <Label className="text-xs">Quantity ({editLine.consumption_unit || editLine.unit})</Label>
                <Input className="mt-1" inputMode="decimal" data-testid="edit-consumption-qty"
                  value={editQty} onChange={(e) => setEditQty(e.target.value)} />
                <p className="mt-1 text-xs text-slate-500">Stock will be adjusted by the difference.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLine(null)}>Cancel</Button>
            <Button data-testid="save-consumption-edit-btn" onClick={saveEditLine}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reverseOpen} onOpenChange={(o) => !o && setReverseOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid="reverse-serials-dialog">
          <DialogHeader><DialogTitle>Reverse roll-serial consumption</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p>
              This will undo <b>every roll-serial consumption logged against this job</b>, restore each
              roll's sqft balance (rolls at zero go back to “received”), and remove those material lines
              from the job. Use this when sqft was logged to the wrong job, then re-log it correctly.
            </p>
            <div className="rounded-md bg-slate-50 px-2.5 py-2">
              {(job.consumption || []).filter((c) => c.serial_id).map((c, i) => (
                <div key={c.id || i} className="text-xs">{c.name} · {Number(c.quantity).toFixed(2)} {c.unit}{c.service ? ` · ${c.service}` : ""}</div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReverseOpen(false)}>Cancel</Button>
            <Button variant="destructive" data-testid="reverse-serials-confirm-btn"
              disabled={reverseBusy} onClick={reverseSerials}>
              {reverseBusy ? "Reversing…" : "Reverse & restore balances"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={!!renameSvc} onOpenChange={(o) => !o && setRenameSvc(null)}>
        <DialogContent className="sm:max-w-md" data-testid="rename-service-dialog">
          <DialogHeader><DialogTitle>Change service</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Replace this service with another configured service.</DialogDescription>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Replace <b>{renameSvc}</b> with another configured service. Any consumption
              logged under it is kept and retagged to the new service.
            </p>
            <Select value={renameTo} onValueChange={setRenameTo}>
              <SelectTrigger data-testid="rename-service-select">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {svcCfg
                  .filter((s) => s.name === renameSvc || !(job.services || []).includes(s.name))
                  .map((s) => (
                    <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameSvc(null)}>Cancel</Button>
            <Button data-testid="save-rename-service-btn" onClick={doRenameService}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteSvc} onOpenChange={(o) => !o && setDeleteSvc(null)}>
        <DialogContent className="sm:max-w-md" data-testid="delete-service-dialog">
          <DialogHeader><DialogTitle>Remove this service?</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Confirm removing this service and its logged consumption.</DialogDescription>
          <p className="text-sm text-slate-600">
            Removing <b>{deleteSvc}</b> will also delete every consumption line logged under it,
            and the consumed stock will be returned to the studio's inventory. This can't be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteSvc(null)}>Cancel</Button>
            <Button variant="destructive" data-testid="confirm-delete-service-btn" onClick={doDeleteService}>
              Remove service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={flagOpen} onOpenChange={setFlagOpen}>
        <DialogContent className="sm:max-w-md" data-testid="flag-hq-installer-dialog">
          <DialogHeader>
            <DialogTitle>Flag as HQ installer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              This raises an HQ-installer due against the studio and notifies them to acknowledge or dispute.
            </p>
            <div>
              <Label className="text-xs">Which HQ installer? (optional)</Label>
              <Select
                value={flagForm.installer_id}
                onValueChange={(v) => setFlagForm((f) => ({ ...f, installer_id: v }))}
              >
                <SelectTrigger className="mt-1" data-testid="flag-installer-select">
                  <SelectValue placeholder="Select HQ installer" />
                </SelectTrigger>
                <SelectContent>
                  {installers
                    .filter((i) => (i.installer_type || "studio") === "hq")
                    .map((i) => (
                      <SelectItem key={i.id} value={i.id}>
                        {i.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Amount owed to HQ (₹)</Label>
              <Input
                className="mt-1"
                inputMode="decimal"
                data-testid="flag-amount-input"
                value={flagForm.amount}
                onChange={(e) => setFlagForm((f) => ({ ...f, amount: e.target.value }))}
              />
              <p className="mt-1 text-xs text-slate-500">Defaults to the job's installer cost.</p>
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Input
                className="mt-1"
                data-testid="flag-note-input"
                value={flagForm.note}
                onChange={(e) => setFlagForm((f) => ({ ...f, note: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFlagOpen(false)}>
              Cancel
            </Button>
            <Button data-testid="confirm-flag-btn" onClick={flagHq}>
              Flag &amp; raise due
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
        <DialogContent className="sm:max-w-md" data-testid="dispute-flag-dialog">
          <DialogHeader>
            <DialogTitle>Dispute HQ-installer flag</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Why is this incorrect?</Label>
            <Input
              data-testid="dispute-note-input"
              value={disputeNote}
              onChange={(e) => setDisputeNote(e.target.value)}
              placeholder="e.g. Our own installer did this job"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeOpen(false)}>
              Cancel
            </Button>
            <Button data-testid="confirm-dispute-btn" onClick={() => respondFlag("dispute", disputeNote)}>
              Send dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CompleteLeadDialog
        open={completeOpen}
        onOpenChange={setCompleteOpen}
        customerId={job.customer_id}
        targetStatus={pendingStatus}
        onCompleted={applyPendingStatus}
      />

      <Dialog open={!!stageConfirm} onOpenChange={(o) => !o && setStageConfirm(null)}>
        <DialogContent className="sm:max-w-md" data-testid="stage-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Change job stage?</DialogTitle>
            <DialogDescription>
              Move this job from <strong className="text-brand-ink">{job.status}</strong> to{" "}
              <strong className="text-brand-ink">{stageConfirm}</strong>? This updates the pipeline
              and any linked tracking.
            </DialogDescription>
          </DialogHeader>
          {stageConfirm === "Booked" && (
            <div data-testid="stage-booked-date-block">
              <Label>Booked for (date) *</Label>
              <Input type="date" className="mt-1.5" value={bookDate} data-testid="stage-booked-date-input"
                min={new Date().toLocaleDateString("en-CA")}
                onChange={(e) => setBookDate(e.target.value)} />
              <p className="mt-1 text-xs text-muted-foreground">
                The job is placed on the Slot Chart for this date (PPF blocks 2 days, Sunfilm 1 day).
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              data-testid="stage-confirm-cancel-btn"
              onClick={() => setStageConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              data-testid="stage-confirm-continue-btn"
              disabled={stageConfirm === "Booked" && !bookDate}
              onClick={async () => {
                const v = stageConfirm;
                setStageConfirm(null);
                await doStatusUpdate(v, v === "Booked" ? { booked_date: bookDate } : {});
              }}
            >
              Let me continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deliverConfirm} onOpenChange={setDeliverConfirm}>
        <DialogContent className="sm:max-w-md" data-testid="deliver-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm delivery and send for warranty registration?</DialogTitle>
            <DialogDescription>
              We check that material consumption is logged for each film service (PPF / Velotint)
              with enough sqft before this job goes to the CRM warranty queue.
            </DialogDescription>
          </DialogHeader>

          {readinessLoading ? (
            <div
              className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600"
              data-testid="deliver-readiness-loading"
            >
              Checking material logs…
            </div>
          ) : readiness && readiness.ok ? (
            <div
              className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
              data-testid="deliver-readiness-ok"
            >
              <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" strokeWidth={2} />
              <div>
                <p className="font-semibold">Material logs complete</p>
                <p className="mt-0.5 text-emerald-700">
                  Every film service has enough sqft logged. This job will be sent to the CRM
                  warranty queue on delivery.
                </p>
              </div>
            </div>
          ) : readiness ? (
            <div
              className="rounded-lg border-2 border-rose-300 bg-rose-50 p-3 text-sm text-rose-800"
              data-testid="deliver-readiness-incomplete"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" strokeWidth={2} />
                <div>
                  <p className="font-semibold">Material logs incomplete</p>
                  <ul className="mt-1 list-disc pl-4 text-rose-700">
                    {(readiness.pending || []).map((p) => (
                      <li key={p} data-testid={`deliver-pending-${p}`}>{p}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-rose-700">
                    Delivering now will keep the warranty on hold (shown in red) until the missing
                    material is logged. Log the material first, or confirm you are overriding.
                  </p>
                </div>
              </div>
              <label
                className="mt-3 flex cursor-pointer items-start gap-2 rounded-md border border-rose-200 bg-white p-2.5"
                data-testid="deliver-override-label"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-rose-600"
                  data-testid="deliver-override-checkbox"
                  checked={matOverride}
                  onChange={(e) => setMatOverride(e.target.checked)}
                />
                <span className="text-xs font-medium text-rose-800">
                  I confirm I am proceeding to Delivered without complete material logs. This
                  override will be recorded.
                </span>
              </label>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" data-testid="deliver-cancel-btn" onClick={() => setDeliverConfirm(false)}>
              Cancel
            </Button>
            <Button
              data-testid="deliver-proceed-btn"
              disabled={readinessLoading || (readiness && !readiness.ok && !matOverride)}
              className={readiness && !readiness.ok ? "bg-rose-600 hover:bg-rose-700" : ""}
              onClick={async () => {
                setDeliverConfirm(false);
                await doStatusUpdate("Delivered");
              }}
            >
              {readiness && !readiness.ok ? "Override & deliver" : "Proceed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={niOpen} onOpenChange={setNiOpen}>
        <DialogContent className="sm:max-w-md" data-testid="not-interested-dialog">
          <DialogHeader>
            <DialogTitle>Mark lead as Not Interested</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Reason</Label>
              <Select
                value={niForm.reason}
                onValueChange={(v) => setNiForm({ ...niForm, reason: v })}
              >
                <SelectTrigger className="mt-1.5" data-testid="not-interested-reason-select">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {niReasons.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">
                Note {niForm.reason === "Other" ? "(required)" : "(optional)"}
              </Label>
              <Input
                className="mt-1.5"
                data-testid="not-interested-note-input"
                placeholder="Add context — stays permanently on the record"
                value={niForm.note}
                onChange={(e) => setNiForm({ ...niForm, note: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNiOpen(false)}>
              Cancel
            </Button>
            <Button data-testid="confirm-not-interested-btn" onClick={saveNotInterested}>
              Mark Not Interested
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, testid }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p data-testid={testid} className="mt-0.5 text-sm font-medium text-brand-ink">
      {value || "—"}
    </p>
  </div>
);

const Section = ({ title, defaultOpen = true, children, testid }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-brand-line bg-white" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testid ? `${testid}-toggle` : undefined}
        className="flex min-h-[56px] w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-slate-50"
      >
        <span className="text-base font-semibold text-brand-ink">{title}</span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="border-t border-brand-line p-4">{children}</div>}
    </div>
  );
};
